import { useCallback, useEffect, useRef, useState } from 'react';

import { debounce } from './ArticleDetailTutorial.utils';

import { useDeviceCheck } from '@/shared/hooks';

const PADDING_VALUE = 2;
const NO_TOOLBAR_HEIGHT = 0;
const DISTANCE_FROM_BOTTOM_END = 9;
const SCRAP_MOBILE_MARGIN = 5;

const checkDOMReady = (): boolean => {
  const article = document.querySelector('article');
  if (!article) return false;

  const paragraphs = article.querySelectorAll<HTMLParagraphElement>('p');
  if (!paragraphs) return false;

  return true;
};

export interface ArticleFirstLineProps {
  text: string;
  style: {
    top: number;
    left: number;
    width: number;
  };
}

const useArticleFirstLine = (content: string): ArticleFirstLineProps | null => {
  const [firstLineData, setFirstLineData] =
    useState<ArticleFirstLineProps | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);

  const getFirstLine = useCallback(() => {
    const isDOMReady = checkDOMReady();
    if (!isDOMReady) return null;
    const article = document.querySelector('article');
    if (!article) return null;

    const pTags = article.querySelectorAll<HTMLParagraphElement>('p');
    const firstP = Array.from(pTags).find((p) => p.textContent?.trim());

    if (!firstP) return null;

    const position = firstP.getBoundingClientRect();
    const fullText = firstP.textContent?.trim() || '';

    return {
      text: fullText,
      style: {
        top: position.top + window.scrollY,
        left: position.left,
        width: firstP.clientWidth + PADDING_VALUE,
      },
    };
  }, []);

  const checkAndUpdateFirstLine = useCallback(() => {
    const newFirstLine = getFirstLine();
    if (
      newFirstLine &&
      JSON.stringify(newFirstLine) !== JSON.stringify(firstLineData)
    ) {
      setFirstLineData(newFirstLine);
    }
  }, [getFirstLine, firstLineData]);

  const handleResize = debounce(checkAndUpdateFirstLine, 200);

  useEffect(() => {
    if (!content) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    checkAndUpdateFirstLine();

    const debouncedCheckAndUpdateFirstLine = debounce(
      checkAndUpdateFirstLine,
      300
    );

    const target = document.querySelector('#article-detail-container');
    if (target) {
      observerRef.current = new MutationObserver(
        debouncedCheckAndUpdateFirstLine
      );
      observerRef.current.observe(target, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [content, checkAndUpdateFirstLine]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return firstLineData;
};

interface ScrapPosition {
  top?: number;
  bottom?: number;
  left: number;
}

const useToolbarScrapPosition = (): ScrapPosition | null => {
  const { isDesktop } = useDeviceCheck();
  const toolbarId = isDesktop ? 'side-toolbar-area' : 'bottom-toolbar-area';
  const [scrapPosition, setScrapPosition] = useState<ScrapPosition | null>(
    null
  );

  const observerRef = useRef<MutationObserver | null>(null);

  const calculatePosition = useCallback(() => {
    const toolbarBox = document.getElementById(toolbarId);
    const scrapButton = toolbarBox?.querySelector('#scrap-button');

    if (!scrapButton || !toolbarBox) return null;

    const position = scrapButton.getBoundingClientRect();

    return isDesktop
      ? { top: position.top, left: position.left }
      : {
          bottom: isDesktop ? NO_TOOLBAR_HEIGHT : DISTANCE_FROM_BOTTOM_END,
          left: position.left + SCRAP_MOBILE_MARGIN,
        };
  }, [isDesktop, toolbarId]);

  const checkAndUpdatePosition = useCallback(() => {
    const newPosition = calculatePosition();
    if (
      newPosition &&
      JSON.stringify(newPosition) !== JSON.stringify(scrapPosition)
    ) {
      setScrapPosition(newPosition);
    }
  }, [calculatePosition, scrapPosition]);

  useEffect(() => {
    checkAndUpdatePosition();

    const debouncedCheckAndUpdatePosition = debounce(checkAndUpdatePosition);

    const target = document.querySelector('#article-detail-toolbar');
    if (target) {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      observerRef.current = new MutationObserver(
        debouncedCheckAndUpdatePosition
      );
      observerRef.current.observe(target, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
          observerRef.current = null;
        }
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop, checkAndUpdatePosition]);

  useEffect(() => {
    const handleResize = debounce(checkAndUpdatePosition, 200);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  return scrapPosition;
};

export { useArticleFirstLine, useToolbarScrapPosition };
